import React, { Fragment, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { styled, SxProp } from 'dripsy';
import { useTranslation } from 'react-i18next';

import { useNotificationsModel } from '@models/Notifications/NotificationsModel';
import { useLink, usePopover } from '@hooks';
import { Notification } from '@models/Notifications/NotificationModel';
import { AnalyticsService } from '@services';
import { NotificationTypeEnum } from '@api';
import { BaseText } from '../../common/BaseText';
import { Line } from '../../common/Line';
import { Button, ButtonStyleEnum } from '../../common/Button';
import { NotificationList } from '../../business/NotificationList';
import { Popover, PopoverPositionEnum } from '../Popover';
import { IconWithBadge } from '../../common/IconWithBadge';
import { EmptyResultTypeEnum } from '../EmptyResultView';
import { Touchable } from '../../common/Touchable';
import { IconTouchable } from '../../common/IconTouchable';
import { Separator } from '../../common/Separator';

const MenuContainer = styled(View)({
  width: 390,
  height: 440,
  bg: '$background0',
});

const HeaderContainer = styled(View)({
  px: '$6',
});

const Header = styled(View)({
  height: 56,
  width: '100%',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const HeaderText = styled(BaseText)({
  fontWeight: 'semi',
  variant: 'text.medium',
  color: '$heading800',
});

const contentContainerStyle: SxProp = {
  px: '$6',
  py: '$6',
  flex: 1,
};

export const NotificationMenu = observer(() => {
  const notifications = useNotificationsModel();
  const { ref, togglePopover, closePopover } = usePopover();
  const { t } = useTranslation();
  const { linkTo } = useLink();

  async function fetchNotifications(hasPopoverBeenOpened: boolean) {
    if (!hasPopoverBeenOpened) {
      return;
    }

    try {
      await notifications.list.fetch.run();
    } catch (error) {
      // Do nothing
    }
  }

  async function markAllAsRead() {
    try {
      await notifications.list.markAllAsRead.run();
    } catch (error) {
      // Do nothing
    }
  }

  async function markItemAsRead(item: Notification) {
    if (!item.isNew) {
      return;
    }

    try {
      await item.markAsRead.run();
    } catch (error) {
      // Do nothing
    }
  }

  function trackNotificationAnalytic(item: Notification) {
    AnalyticsService.trackNotification(item.message);
  }

  const navigateByNotification = useCallback((item: Notification) => {
    // TODO: add correct notification handling
    if (item.type === NotificationTypeEnum.NewMention) {
      markItemAsRead(item);
      closePopover();
      return;
    }
    markItemAsRead(item);
    trackNotificationAnalytic(item);

    linkTo(item.url);
    closePopover();
  }, []);

  const isLoading =
    notifications.list.fetch.inProgress && notifications.list.isEmpty;

  return (
    <View>
      <Popover
        ref={ref}
        position={PopoverPositionEnum.BottomRight}
        onVisibleChange={fetchNotifications}
      >
        <Fragment>
          <Separator size="$5" />
          {notifications.hasNewNotifications ? (
            <Touchable borderless onPress={togglePopover}>
              <IconWithBadge
                name="notification-light"
                text={notifications.formattedNewCount}
              />
            </Touchable>
          ) : (
            <IconTouchable
              onPress={togglePopover}
              name="notification-light"
            />
          )}
          <Separator size="$5" />
        </Fragment>

        <MenuContainer>
          <HeaderContainer>
            <Header>
              <HeaderText>
                {t('common.webHeader.notifications')}
              </HeaderText>
              <Button
                buttonStyle={ButtonStyleEnum.Ghost}
                iconRight="done-circule-light"
                disabled={!notifications.hasNewNotifications}
                onPress={markAllAsRead}
              >
                {t('common.webHeader.markAllAsRead')}
              </Button>
            </Header>
            <Line />
          </HeaderContainer>
          <NotificationList
            isLoading={isLoading}
            data={notifications.list.asArray}
            contentContainerSx={contentContainerStyle}
            onPress={navigateByNotification}
            emptyResultType={EmptyResultTypeEnum.NoNotifications}
          />
        </MenuContainer>
      </Popover>
    </View>
  );
});
